import Controller, { OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { PanelAPI, TabControlAPI, TabWithContentAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Inject } from '@odinjs/odin';
import { ScoreAndIndexesService, ScoreListContext } from './scores-list-service';
import { ScoreIndexesRulesVisualization } from '../score-and-indexes-params';
import BaseController from '../../BaseController';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { ScoresListAliases as Aliases, FragmentNames } from '../../../codegen/index';
import { ScoreType } from '../scores-references/score-reference-controller';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ItemPepType, RulesItemConfiguration } from '../../../constants/utils-pep';
import { YesNoType }  from '../../../utils/enum-utils';

@Controller({ component: Aliases.LAYOUT_SCORES, fragment: FragmentNames.SCORES_LIST })
export default class ScoresListController extends BaseController {

  @Inject(Aliases.TAB_CONTROL)
  private tabControlAPI!: TabControlAPI;

  @Inject(Aliases.PANEL_TAB_CONTROL)
  private panelTabControl!: PanelAPI;

  rulesVisualization!: ScoreIndexesRulesVisualization[]

  @OnMount({ target: Aliases.LAYOUT_SCORES })
  handleMount(): void {
    InformationBarUtils.collapsedBar = true;
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId });
    this.checkItemEditPermission();
    this.fetchScoreIndexesVisualizationRules()
  }

  private checkItemEditPermission(): Promise<void> {
    const itemType = ItemPepType.SCORES_INDEXES;
    return HttpService.get(Endpoint.PEP_CAN_EDIT_ITEM, { itemType })
      .then(response => {
        const configuration = response.body as RulesItemConfiguration;
        const isAllowEdit = configuration?.allowEditing === YesNoType.YES;
        ScoreAndIndexesService.isAllowEdit = isAllowEdit;
      })
  }

  @OnMount(Aliases.TAB_WITH_CONTENT_SCORE_DINI)
  handleMountTabWithContentScoreDini(event: MountEvent): void {
    const tabApi = event.target as TabWithContentAPI;
    this.activeTab(tabApi, ScoreType.SCORE_DINI)
  }

  @OnMount(Aliases.TAB_WITH_CONTENT_SCORE_MEWS)
  handleMountTabWithContentScoreMews(event: MountEvent): void {
    const tabApi = event.target as TabWithContentAPI;
    this.activeTab(tabApi, ScoreType.SCORE_MEWS)
  }

  @OnMount(Aliases.TAB_WITH_CONTENT_SCORE_MORSE)
  handleMountTabWithContentScoreMorse(event: MountEvent): void {
    const tabApi = event.target as TabWithContentAPI;
    this.activeTab(tabApi, ScoreType.SCORE_MORSE)
  }

  @OnMount(Aliases.TAB_WITH_CONTENT_SCORE_PEWS)
  handleMountTabWithContentScorePews(event: MountEvent): void {
    const tabApi = event.target as TabWithContentAPI;
    this.activeTab(tabApi, ScoreType.SCORE_PEWS)
  }

  @OnMount(Aliases.TAB_WITH_CONTENT_SCORE_TISS_28)
  handleMountTabWithContentScoreTiss(event: MountEvent): void {
    const tabApi = event.target as TabWithContentAPI;
    this.activeTab(tabApi, ScoreType.SCORE_TISS_28)
  }

  @OnMount(Aliases.TAB_WITH_CONTENT_SCORE_NAS)
  handleMountTabWithContentScoreNas(event: MountEvent): void {
    const tabApi = event.target as TabWithContentAPI;
    this.activeTab(tabApi, ScoreType.SCORE_NAS)
  }

  @OnMount(Aliases.TAB_WITH_CONTENT_SCORE_NRS)
  handleMountTabWithContentScoreNrs(event: MountEvent): void {
    const tabApi = event.target as TabWithContentAPI;
    this.activeTab(tabApi, ScoreType.SCORE_NRS)
  }

  @OnMount(Aliases.TAB_WITH_CONTENT_SCORE_FLEX)
  handleMountTabWithContentScoreFlex(event: MountEvent): void {
    const tabApi = event.target as TabWithContentAPI;
    this.activeTab(tabApi, ScoreType.SCORE_FLEX)
  }

  @OnUnmount(Aliases.LAYOUT_SCORES)
  handleUnmountRoot(): void {
    ScoreListContext.clean()
  }

  @OnMount(Aliases.TAB_CONTROL)
  handleMountTabControl(): void {
    const rulesVisualization = this.context.get('rulesVisualization');
    const currentTab = ScoreListContext.currentTab;
    this.tabControlAPI.setActiveTab(!currentTab ? rulesVisualization[0].idScoreIndexes : currentTab);
    ScoreListContext.currentTab = this.tabControlAPI.getActiveTab()
  }

  private fetchScoreIndexesVisualizationRules (): Promise<void> {
    return HttpService.get(Endpoint.SCORE_INDEXES_RULES_VISUALIZATION)
      .then(response => {
        const scoreIndexesRules = response.body as ScoreIndexesRulesVisualization[];
        const orderScoreIndexesRules = scoreIndexesRules.sort((a, b) => parseInt(a.idScoreIndexes) - parseInt(b.idScoreIndexes))
        this.rulesVisualization = orderScoreIndexesRules;
        this.context.set({ rulesVisualization: this.rulesVisualization})
        this.panelTabControl.setVisible(true)
      }).catch((error) => {
        this.showToast(error?.body.errorMessage, ToastLevel.ERROR)
      })
  }

  private activeTab(tab: TabWithContentAPI, idScoreIndexes: string): void {
    const scoreIndexesRulesVisualization = this.rulesVisualization.find((item) => item.idScoreIndexes === idScoreIndexes);
    if (scoreIndexesRulesVisualization != null) {
      tab.setVisible(true);
      tab.setActiveTab(idScoreIndexes)
    }
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-nas-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}
